/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * listings.ts — 100% Bridge API (MySQL) version
 * Supabase completely removed. All data from PHP Bridge API.
 */
import { bridgeApi } from '@/lib/bridgeApi';
import { parseIdFromSlug } from '@/lib/seo';

export type ListingType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export const TYPE_META: Record<number, { label: string; slug: string; gradient: string; accent: string }> = {
  1:  { label: "Hospital",          slug: "hospital",          gradient: "from-sky-500 to-cyan-500",       accent: "sky" },
  2:  { label: "Doctor",            slug: "doctor",            gradient: "from-emerald-500 to-teal-500",   accent: "emerald" },
  3:  { label: "Pharmacy",          slug: "pharmacy",          gradient: "from-indigo-500 to-violet-500",  accent: "indigo" },
  4:  { label: "Home Care",         slug: "homecare",          gradient: "from-pink-500 to-rose-500",      accent: "pink" },
  6:  { label: "Diagnostic Centre", slug: "diagnostic-center", gradient: "from-fuchsia-500 to-purple-500", accent: "fuchsia" },
  7:  { label: "Wholesaler",        slug: "wholesaler",        gradient: "from-amber-500 to-orange-500",   accent: "amber" },
  8:  { label: "Blood Bank",        slug: "blood-bank",        gradient: "from-rose-600 to-red-600",       accent: "rose" },
  9:  { label: "Health Camp",       slug: "health-camp",       gradient: "from-green-500 to-teal-500",     accent: "green" },
  10: { label: "Ambulance",         slug: "ambulance",         gradient: "from-red-500 to-orange-500",     accent: "red" },
};

export interface ListingV2 {
  id: string;
  owner_user_id?: string | null;
  type_code: number;
  business_name: string;
  slug: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  canonical_url?: string;
  seo_index?: boolean;
  description: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  latitude: number | null;
  longitude: number | null;
  rating: number;
  review_count: number;
  view_count?: number;
  is_verified: boolean;
  is_featured: boolean;
  city?: { id: string; name: string; slug: string } | null;
  state?: { id: string; name: string; slug: string } | null;
  area?: string | null;
  pincode?: string | null;
  cover_image: string | null;
  /** 'logo' | 'card_photo' | 'gallery' | null — drives contain-vs-cover
   * display (a Logo shouldn't be force-cropped the way a real photo is). */
  cover_image_type?: string | null;
  gallery: string[];
  /** Index-aligned with gallery — the image_type of each gallery entry. */
  galleryTypes?: (string | null)[];
  tagline?: string;
  highlights?: string[];
  services?: string[];
  experience_years?: number;
  consultation_fee?: number | null;
  languages?: string[];
  details?: Record<string, any>;
  booking_enabled?: boolean;
  hours_enabled?: boolean;
}


export interface ListingFilters {
  typeCode?: number | null;
  cityId?: string | null;
  search?: string | null;
  verifiedOnly?: boolean;
  page?: number;
  pageSize?: number;
  /** Opt-in "within X km" radius — additive on top of city/search, never a replacement.
   * Only providers with their own lat/lng on file (Profile -> Location on Map) can match. */
  radius?: { lat: number; lng: number; km: number } | null;
}

// Helper: decode HTML entities (handles double-encoded &amp;amp; → &amp; → &)
export function decodeHtml(s: string | null | undefined): string {
  if (!s) return '';
  let prev = '';
  let r = String(s);
  while (prev !== r) {
    prev = r;
    r = r.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&#039;/g, "'").replace(/&apos;/g, "'");
  }
  return r;
}

// Helper: make a city/state slug from name
function toSlug(name: string): string {
  return (name ?? '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

// Helper: extract image URL — skip placeholder paths
function cleanImage(url: string | null | undefined): string | null {
  if (!url) return null;
  const u = url.toLowerCase();
  if (
    u.includes('placeholder') ||
    u.endsWith('/placeholder.svg') ||
    u.includes('thumb_154_154_placeholder')
  ) {
    return null;
  }
  return url;
}

/**
 * Map a Bridge API listing row (from `listings` or `listing` action) → ListingV2
 * Bridge API returns camelCase from formatListingResponse:
 *   name, categoryType, isVerified, isPremium, city, state, city_id, state_id,
 *   phone, altPhone, email, profile_email, whatsapp, address, image, banner,
 *   businessHours, openingTime, closingTime, youtubeVideo, social, tagline,
 *   views, rating, review_count, slug, user_id (= owner_user_id in providers)
 */
function mapBridgeRow(r: any): ListingV2 {
  // Gallery: primary image first, then gallery array items. galleryTypes stays
  // index-aligned with gallery — lets the detail page know WHICH selected
  // thumbnail is a Logo (not just whichever happens to be first), so it can
  // render that one uncropped regardless of its position in the strip.
  const gallery: string[] = [];
  const galleryTypes: (string | null)[] = [];
  // Gallery items pehle (local localhost URL hoti hai — correct)
  if (Array.isArray(r.gallery) && r.gallery.length > 0) {
    for (const g of r.gallery) {
      const url = cleanImage(typeof g === 'string' ? g : g?.url ?? g?.fullUrl);
      if (url && !gallery.includes(url)) {
        gallery.push(url);
        galleryTypes.push(typeof g === 'string' ? null : (g?.image_type ?? null));
      }
    }
  }
  // r.image sirf tab use karo jab gallery bilkul empty ho
  if (gallery.length === 0) {
    const mainImg = cleanImage(r.image ?? r.banner);
    if (mainImg) { gallery.push(mainImg); galleryTypes.push(r.cover_image_type ?? null); }
  }
  // HomeCare providers store their photo separately (photo_url), not in the
  // generic image/banner columns — fall back to it so a provider who's only
  // set a profile photo (no gallery yet) still shows something on the public listing.
  if (gallery.length === 0) {
    const photoFallback = cleanImage(r.photoUrl);
    if (photoFallback) { gallery.push(photoFallback); galleryTypes.push(null); }
  }

  // Build details object from all available fields
  const details: Record<string, any> = {};

  // Generic
  if (r.businessHours ?? r.business_hours) details.business_hours  = r.businessHours ?? r.business_hours;
  if (r.openingTime)    details.opening_time    = r.openingTime;
  if (r.closingTime)    details.closing_time    = r.closingTime;
  if (r.closedOn)       details.closed_on       = r.closedOn;
  if (r.youtubeVideo)   details.youtube_video   = r.youtubeVideo;
  if (r.social)         details.social          = r.social;
  if (r.tagline)        details.tagline         = r.tagline;
  if (r.website)        details.website         = r.website;
    if (r.whatsapp)       details.whatsapp        = r.whatsapp;
  if (r.altPhone)       details.alt_phone       = r.altPhone;

  // Advanced sections — API returns these as top-level keys, must map to details
  if (r.achievements)            details.achievements        = r.achievements;
  if (r.testimonials)            details.testimonials        = r.testimonials;
  if (r.insurance_partners)      details.insurance_partners  = r.insurance_partners;
  if (r.before_after_cases)      details.before_after_cases  = r.before_after_cases;
  if (r.google_rating != null)   details.google_rating       = r.google_rating;
  if (r.google_review_count != null) details.google_review_count = r.google_review_count;
  if (r.google_place_id)         details.google_place_id     = r.google_place_id;
  if (r.youtube_videos)          details.youtube_videos      = r.youtube_videos;
  if (r.social_facebook)         details.social_facebook     = r.social_facebook;
  if (r.social_instagram)        details.social_instagram    = r.social_instagram;
  if (r.social_twitter)          details.social_twitter      = r.social_twitter;
  if (r.social_linkedin)         details.social_linkedin     = r.social_linkedin;
  if (r.social_youtube)         details.social_youtube      = r.social_youtube;

  // Offers / Ads — all types
  if (r.offers_ads)             details.offers_ads           = r.offers_ads;

  // Medical Store / Wholesaler (type 3, 7) — PHP returns camelCase
  if (r.shopName)               details.shop_name            = r.shopName;
  if (r.drugLicense)            details.drug_license         = r.drugLicense;
  if (r.gstNumber)              details.gst_number           = r.gstNumber;
  if (r.tinNumber)              details.tin_number           = r.tinNumber;
  if (r.homeDelivery !== undefined) details.home_delivery    = r.homeDelivery;
  if (r.products)               details.products             = r.products;
  if (r.brands)                 details.brands               = r.brands;
  if (r.productCategories)      details.product_categories   = r.productCategories;
  if (r.brandsDealt)            details.brands_dealt         = r.brandsDealt;
  if (r.minimumOrder)           details.minimum_order        = r.minimumOrder;
  if (r.deliveryAreas)          details.delivery_areas       = r.deliveryAreas;

  // Diagnostic Centre (type 6) — PHP returns camelCase
  if (r.nablAccredited !== undefined) details.nabl_accredited = r.nablAccredited;
  if (r.nablNumber)             details.nabl_number          = r.nablNumber;
  if (r.testsOffered)           details.tests_offered        = r.testsOffered;
  if (r.homeCollection !== undefined) details.home_collection = r.homeCollection;
  if (r.established_year)       details.established_year     = r.established_year;

  // Doctor
  if (r.specialization)   details.specialization    = decodeHtml(r.specialization);
  if (r.specializationId) details.specialization_id = r.specializationId;
  if (r.qualification)    details.qualification     = decodeHtml(r.qualification);
  if (r.experience)       details.experience_years  = r.experience;
  if (r.consultationFee)  details.consultation_fee  = r.consultationFee;
  if (r.clinicName)       details.clinic_name       = decodeHtml(r.clinicName);
  if (r.hospitalAffiliations) details.hospital_affiliations = decodeHtml(r.hospitalAffiliations);
  if (r.memberships)      details.memberships       = decodeHtml(r.memberships);
  if (r.languages)        details.languages         = r.languages;
  if (r.awards)           details.awards            = r.awards;
  if (r.certifications)   details.certifications    = r.certifications;


  // Hospital / Diagnostic
  if (r.registrationNo)    details.registration_no      = r.registrationNo;
  if (r.noOfBeds)          details.no_of_beds            = r.noOfBeds;
  if (r.noOfIcus)          details.no_of_icus            = r.noOfIcus;
  if (r.specialities)      details.specialities          = r.specialities;
  if (r.facilities)        details.facilities            = r.facilities;
  if (r.services)          details.services              = r.services;
  if (r.insuranceCompanies)details.insurance_companies   = r.insuranceCompanies;
  if (r.tpaPartners)       details.tpa_partners          = r.tpaPartners;
  if (r.governmentSchemes) details.government_schemes    = r.governmentSchemes;

  // Blood Bank (type 8)
  if (r.bloodTypes)    details.blood_types  = r.bloodTypes;
  if (r.bloodStock)    details.blood_stock  = r.bloodStock;
  if (r.licenseNo)     details.license_no   = r.licenseNo;
  if (r.donorCount)    details.donor_count  = r.donorCount;
  if (r.emergency !== undefined) details.emergency = r.emergency;

  // Ambulance (type 5 & 10)
  if (r.vehicleTypes)  details.vehicle_types  = r.vehicleTypes;
  if (r.vehicles)      details.vehicles       = r.vehicles;
  if (r.coverageArea)  details.coverage_area  = r.coverageArea;
  if (r.returnTrips !== undefined) details.return_trips = r.returnTrips;
  if (r.returnDiscountRate != null) details.return_discount_rate = r.returnDiscountRate;
  // Per-vehicle return routes (real from/to city, date, ambulance type, discount) —
  // a provider can have more than one active return trip, one per vehicle.
  if (r.returnTripRoutes) details.return_trip_routes = r.returnTripRoutes;
  if (r.ambulanceType) details.ambulance_type = r.ambulanceType;
  if (r.vehicleCount)  details.vehicle_count  = r.vehicleCount;
  if (r.equipment)     details.equipment      = r.equipment;
   if (r.serviceAreas)  details.service_areas  = r.serviceAreas;
  if (r.baseCharge != null && r.baseCharge !== '')   details.base_charge    = r.baseCharge;
  if (r.perKmCharge != null && r.perKmCharge !== '') details.per_km_charge  = r.perKmCharge;
  if (r.vehicleCount != null) details.vehicle_count  = r.vehicleCount;
  if (r.is24x7 !== undefined) details.is_24x7        = r.is24x7;

  // Health Camp (type 9)
  if (r.events)                         details.events             = r.events;
  if (r.organizationType)               details.organization_type  = r.organizationType;
  if (r.organizerName)                  details.organizer_name     = r.organizerName;
  if (r.organizerType)                  details.organizer_type     = r.organizerType;
  if (r.campSchedule)                   details.camp_schedule      = r.campSchedule;
  if (r.registrationFee != null)        details.registration_fee   = r.registrationFee;
  if (r.isPaid != null)                 details.is_paid            = r.isPaid;
  if (r.eventDate)                      details.event_date         = r.eventDate;
  if (r.startTime)                      details.start_time         = r.startTime;
  if (r.endTime)                        details.end_time           = r.endTime;
  if (r.venueName)                      details.venue_name         = r.venueName;
  if (r.diseaseTypes)                   details.disease_types      = r.diseaseTypes;
  if (r.freeServices)                   details.free_services      = r.freeServices;
  if (r.whatsappNumber)                 details.whatsapp_number    = r.whatsappNumber;

  // HomeCare (type 4)
  if (r.hourlyRate)              details.hourly_rate     = r.hourlyRate;
  if (r.dailyRate)               details.daily_rate      = r.dailyRate;
  if (r.monthlyRate)             details.monthly_rate    = r.monthlyRate;
  if (r.availability)            details.availability    = r.availability;
  if (r.kycVerified !== undefined) details.kyc_verified  = r.kycVerified;
  if (r.providerType)            details.provider_type   = r.providerType;
  if (r.bio)                     details.bio             = r.bio;
  if (r.serviceArea)             details.service_area    = r.serviceArea;
  if (r.photoUrl)                details.photo_url       = r.photoUrl;

  // Merge API-provided details JSON with manually extracted fields
  // r.details from PHP formatListingResponse takes priority
  const mergedDetails = {
  ...(r.details && typeof r.details === 'object' ? r.details : {}),
  ...details,
};

  return {
    id:            String(r.id),
    // Bridge API returns user_id = owner_user_id (from formatListingResponse)
    owner_user_id: r.user_id && Number(r.user_id) > 0 ? String(r.user_id) : null,
    type_code:     Number(r.categoryType ?? r.type_code ?? 1),
    business_name: decodeHtml(r.name ?? r.business_name ?? ''),
    slug:          r.slug ?? String(r.id),
    meta_title:       r.meta_title || undefined,
    meta_description: r.meta_description || undefined,
    meta_keywords:    r.meta_keywords || undefined,
    canonical_url:    r.canonical_url || undefined,
    seo_index:        r.seo_index !== undefined ? !!r.seo_index : true,
    description:   decodeHtml(r.description) ?? null,
    address:       r.address ?? null,
    phone:         r.phone ?? null,
    email:         r.email ?? r.profile_email ?? null,
    latitude:      r.latitude  ? Number(r.latitude)  : null,
    longitude:     r.longitude ? Number(r.longitude) : null,
    rating:        Number(r.rating ?? 0),
    review_count:  Number(r.review_count ?? 0),
    view_count:    Number(r.views ?? r.view_count ?? 0),
    is_verified:   !!(r.isVerified ?? r.is_verified),
    is_featured:   !!(r.isPremium  ?? r.is_featured),
    city:  r.city  ? { id: String(r.city_id  ?? ''), name: r.city,  slug: toSlug(r.city)  } : null,
    state: r.state ? { id: String(r.state_id ?? ''), name: r.state, slug: toSlug(r.state) } : null,
    area:    r.area    ? String(r.area)    : null,
    pincode: r.pincode ? String(r.pincode) : null,
    cover_image:        gallery[0] ?? null,
    // Only meaningful when it's actually describing gallery[0] — once real
    // gallery items exist, gallery[0] is the true primary photo from the
    // photos table and this still matches it (same is_primary row backend
    // resolves both from). When gallery[] was empty and we fell back to
    // r.image, this is that same photo's type too (formatListingResponse
    // sets 'image' and 'cover_image' from the identical resolved value).
    cover_image_type:   r.cover_image_type ?? null,
    gallery,
    galleryTypes,
     tagline:            decodeHtml(r.tagline ?? ''),
    highlights: (() => {
      const h: string[] = [];
      const spec = r.specialization ?? r.details?.specialization;
      const qual = r.qualification  ?? r.details?.qualification;
      if (spec && String(spec).trim()) h.push(decodeHtml(String(spec).trim()));
      if (qual && String(qual).trim()) h.push(decodeHtml(String(qual).trim()));
      return h;
    })(),
    services:           Array.isArray(r.services) ? r.services : (r.services ? [r.services] : []),
    experience_years:   r.experience ? Number(r.experience) : undefined,
    consultation_fee:   r.consultationFee ? Number(r.consultationFee) : null,
    languages:          Array.isArray(r.languages) ? r.languages : [],
    details: mergedDetails,
    booking_enabled: r.booking_enabled == null ? undefined : Boolean(r.booking_enabled),
    hours_enabled:   r.hours_enabled   == null ? undefined : Boolean(r.hours_enabled),
  };
}


// ─── Public API ─────────────────────────────────────────────────────────────

export async function fetchListings(filters: ListingFilters = {}) {
  const { typeCode, cityId, search, page = 1, pageSize = 24, radius } = filters;
  const radiusParams: Record<string, any> = radius
    ? { lat: radius.lat, lng: radius.lng, radius_km: radius.km }
    : {};

  // "All" tab — fetch from all 5 managed types in parallel and merge
  if (!typeCode) {
    const managedTypes = [1, 2, 3, 6, 7];
    const results = await Promise.allSettled(
      managedTypes.map(t => {
        const p: Record<string, any> = { limit: Math.ceil(pageSize / managedTypes.length), page: 1, type: t, ...radiusParams };
        if (cityId)         p.city_id = cityId;
        if (search?.trim()) p.search  = search.trim();
        return bridgeApi.call<any>('listings', p);
      })
    );
    const allRows: any[] = [];
    let total = 0;
    results.forEach(r => {
      if (r.status === 'fulfilled' && r.value?.data) {
        allRows.push(...r.value.data);
        total += Number(r.value.pagination?.total ?? 0);
      }
    });
    return { items: allRows.map(mapBridgeRow), total };
  }

  // Specific type selected
  const params: Record<string, any> = { limit: pageSize, page, type: typeCode, ...radiusParams };
  if (cityId)         params.city_id = cityId;
  if (search?.trim()) params.search  = search.trim();

  const result = await bridgeApi.call<any>('listings', params);
  const rows: any[] = result?.data ?? [];
  return {
    items: rows.map(mapBridgeRow),
    total: result?.pagination?.total ?? rows.length,
  };
}

export async function fetchListingBySlug(slug: string): Promise<ListingV2 | null> {
  // If slug is purely numeric, skip the slug lookup (would always 404) and go straight to ID
  const isNumericSlug = /^\d+$/.test(slug);

  if (!isNumericSlug) {
    try {
      const result = await bridgeApi.call<any>('listing', { slug });
      const row = result?.data ?? result;
      if (row && row.id) return mapBridgeRow(row);
    } catch {
      // fall through to numeric id fallback
    }
  }

  // Fallback: extract numeric ID from end of slug (e.g. "sunshine-mother-child-mumbai-5" → 5)
  const numId = isNumericSlug ? parseInt(slug, 10) : (parseIdFromSlug(slug) ?? parseInt(slug, 10));
  if (!isNaN(numId) && numId > 0) {
    try {
      const result = await bridgeApi.call<any>('listing', { id: numId });
      const row = result?.data ?? result;
      if (row && row.id) return mapBridgeRow(row);
    } catch {
      // not found
    }
  }

  return null;
}

export async function fetchCitiesAndStates() {
  const [citiesRes, statesRes] = await Promise.allSettled([
    bridgeApi.call<any>('cities', {}),
    bridgeApi.call<any>('states', {}),
  ]);

  // Bridge API cities: [{id, title, state_id, state_name, is_popular, ...}]
  // Frontend needs: [{id, name, slug, state_id}]
  const rawCities: any[] = citiesRes.status === 'fulfilled'
    ? (citiesRes.value?.data ?? citiesRes.value ?? [])
    : [];
  const rawStates: any[] = statesRes.status === 'fulfilled'
    ? (statesRes.value?.data ?? statesRes.value ?? [])
    : [];

  const cities = rawCities.map((c: any) => ({
    id:       String(c.id),
    name:     c.name ?? c.title ?? '',       // API returns 'title', normalize to 'name'
    slug:     toSlug(c.name ?? c.title ?? ''),
    state_id: String(c.state_id ?? ''),
  }));

  const states = rawStates.map((s: any) => ({
    id:   String(s.id),
    name: s.name ?? s.title ?? '',
    slug: toSlug(s.name ?? s.title ?? ''),
  }));

  return { cities, states };
}
/** Live blood stock for a blood-bank provider (type_code 8). */
export async function fetchBloodStock(providerId: string) {
  try {
    const result = await bridgeApi.call<any>('blood_stock_list', { provider_id: providerId });
    const data: any[] = result?.data ?? result ?? [];
    if (!Array.isArray(data)) return [];
    const order = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];
    return data.slice().sort(
      (a: any, b: any) => order.indexOf(a.blood_group) - order.indexOf(b.blood_group)
    );
  } catch { return []; }
}
/** Upcoming / recent health camps organised by a provider (type_code 9). */
export async function fetchProviderCamps(providerId: string) {
  try {
    const result = await bridgeApi.call<any>('health_camps_list', { provider_id: providerId });
    const data: any[] = result?.data ?? result ?? [];
    return Array.isArray(data) ? data : [];
  } catch { return []; }
}